<!--
 * @Author: Victor
 * @Date: 2024-05-20 18:25:59
 * @LastEditors: Merlin
 * @LastEditTime: 2025-04-21 14:52:59
 * @Description: 
-->
<template>
    <NuxtLayout name="casino">
        <div class="dice-game">
            <ClientOnly>
                <GameSettingsDiceTabsWrapper class="flex !justify-end !mb-3">
                    <ClientOnly>
                        <GameSettingsButtonLiveChat v-if="userStore.isLoggedIn" v-model="isOpenLiveChat" />
                    </ClientOnly>
                    <GameSettingsButtonFast v-model="game.dice.fast" />
                    <GameSettingsButtonFairness v-model="isOpenFairness" />
                    <GameSettingsButtonRule v-model="isOpenRule" name="dice" />
                    <GameSettingsButtonVocie
                        v-model="settingStore.inhouseGames.sound.on"
                        v-model:voice-model-value="settingStore.inhouseGames.sound.music"
                        :saveAfterChange="true"
                    ></GameSettingsButtonVocie>
                </GameSettingsDiceTabsWrapper>
            </ClientOnly>
            <div class="w-full relative rounded-md rounded-borders select-none">
                <ClientOnly>
                    <CasinoGameBarJackpot
                        game-name="Dice"
                        :jackpotSize="oGlobalStore?.jackpotSum['dice']"
                    >
                        <div class="w-full flex justify-between text-[#55667f]">
                            <small
                                class="text-xs py-[1px] px-[2px] mr-0.5 rounded-[5px] font-semibold"
                                v-for="(item, ind) in Array(8).fill('5x')"
                                :key="ind"
                                :class="{'active': joinStore.jackpotDelay > ind}"
                            >
                                {{ item }}
                            </small>
                        </div>
                    </CasinoGameBarJackpot>
                    <HistoryBar />
                    <Board
                        :is-submiting="oForm.isSubmiting"
                        :is-under="isUnder"
                        :progress="progress"
                        :iAnimDuration="iAnimDuration"
                        :isFast="isFast"
                        :oGameResult="oGameResult"
                    />
                    <!-- ↓ 滑杆 -->
                    <Slider
                        v-model:progress="progress"
                        :is-under="isUnder"
                        :is-submiting="oForm.isSubmiting"
                    />
                    <!-- ↓ 模式切换器（已经简化为反现提示栏了） -->
                    <Controls
                        class="controls"
                        :is-submiting="oForm.isSubmiting"
                        :amount="oForm.amount"
                        v-model:is-under="isUnder"
                        :progress="progress"
                        v-model:mutipler="mutipler"
                    />
                    <!-- ↓ 下注输入框 -->
                    <CasinoBetOptionSection
                        class="mt-8"
                        :betTookit="betTookit"
                        :auto-bet-tool-kit="autoBetToolKit"
                        v-model:bet-amount-modal-value="oForm.amount"
                        v-model:is-submiting="oForm.isSubmiting"
                        :sound="settingStore.globalSounds"
                        :isAuto="isAuto"
                        :bet-submit="betSubmit"
                        :is-fast="isFast"
                        :expected-reward="expectedReward"
                        @change-mode="toChangeMode"
                    />
                </ClientOnly>
            </div>
        </div>
    </NuxtLayout>
    <!-- ↓ 自动下注的输入面板（弹窗） -->
    <CasinoAutoSection
        :is-fast="isFast"
        :is-submiting="oForm.isSubmiting"
        :is-auto="isAuto"
        v-model:amount="oForm.amount"
        :ref="el => oAutoDialog=el"
    />
    <ModalWinJackpotHandler
        :gameName="'dice'"
        :ref="ev => jackpotHandler = ev"
    />
</template>

<script setup lang="ts">
import useSettingsStore from '~/stores/settings';
import { useUserStore } from '~/stores/user';
import { useJoinStateStore } from '~/stores/cansino/joinState';
import useGlobalStore from '@/stores/global';
import { useAutoOptionStore } from '~/stores/cansino/autoOption';
// ↑ store -----------------------------------------------------------------
import winSound from '~/assets/mp3/dice/dice-win.mp3';
import loseSound from '~/assets/mp3/dice/dice-losing.mp3';
import placeBetSound from '~/assets/mp3/dice/dice-place-bet.mp3';
import gameGeneralBgm from '@/assets/mp3/game_general_bgm.mp3';
// ↑ music -----------------------------------------------------------------
import Board from './_components/Board.vue';
import Controls from './_components/Controls.vue';
import Slider from './_components/Slider.vue';
import HistoryBar from './_components/HistoryBar.vue';
// ↑ .vue file -----------------------------------------------------------------
import { useBettingToolkit } from '~/common/js/bettingToolkit';
import { useAutoBetToolKit } from '~/common/js/bettingToolkit/auto';
import { useGameSocket } from '~/socket/game';
import {betableCheck, showTips4BadAmountIfNeeded} from '@/common/js/betHelper';


const isOpenFairness = ref(false);
const isOpenRule = ref(false);
const isOpenLiveChat = ref(false);
const settingStore = useSettingsStore();
const joinStore = useJoinStateStore();
const oGlobalStore = useGlobalStore();

const { game } = storeToRefs(settingStore);
const { EMIT_DICE_BET } = useGameSocket();
const isUnder = ref(true);
const progress = ref(50);
const mutipler = ref(2);
const oGameResult = ref({});
const userStore = useUserStore();
const jackpotHandler = ref(null);

const isFast = computed(()=>{
    return settingStore.game.dice.fast;
});
const iAnimDuration = computed(()=>{ // 动画执行耗时
    if (isFast.value) return 310; 
    return 1_400;
});
const isAuto = computed(()=>{ // 是否为自动模式
    const bResult = game.value.dice.auto.default;
    return bResult;
});

// ↓返现金额预测
const expectedReward = computed(()=>{
    const result = (
        oGlobalStore.screenMode == 'sm'
        ? void 0
        : mutipler.value * oForm.amount
    );
    return result;
});

const oMusicLib = {
    bg: {
        src: gameGeneralBgm,
        loop: true,
        isBGM: true, // true-false 表示音量取自 globalVolume, globalSoundEffects
    },
    win: {
        src: winSound,
    },
    lose: {
        src: loseSound,
    },
    bet: {
        src: placeBetSound,
    },
};
let musicControler = {} as typeof MusicControler.prototype;

const oForm = reactive({
    amount: 0,
    isSubmiting: false,
});

// ↓ 自动模式输入面板“窗口化改造” 添加的变量与方法： 
const oAutoDialog = ref(null);
const toChangeMode = (isAuto)=>{
    settingStore.game.dice.auto.default = !!isAuto;
    if (isAuto){
        oAutoDialog.value.switchAutoDialogVisible(true);
    }
};

const betTookit = useBettingToolkit(oForm);
// 监听两个字段的变化
watch([oForm?.amount, mutipler], ([newVal1, newVal2]) => {
    const diceMaxWin = Number(oForm.amount.toFixed(2)) * Number(mutipler.value)
    // ↓ 调取 查询当前游戏当前币种最大赔付金额
    showTips4BadAmountIfNeeded(diceMaxWin)
});

// ↓重要方法，提交下注信息 
const betSubmit = async () => { 
    if (!betableCheck(oForm)) return;
    musicControler.oAudio.bet.toPlay();
    const ts = userStore.queueStart(); // 防止余额立即变化 
    oGameResult.value = {};
    oForm.isSubmiting = true;
    let amount = getFixed(oForm.amount, 2);
    // if (import.meta.env.DEV_MODE && isAuto.value && Math.random() <= 0.2){ 
    //     amount = '0'; 
    // }
    const res = await EMIT_DICE_BET({
        amount,
        is_under: isUnder.value,
        isFast: isFast.value,
        frontID: Date.now(),
        prediction: progress.value,
        strategyName: '', // 策略名称
    });
    if (import.meta.dev) {
        console.log('dice 下注结果：\n', res?.response);
    }
    if (!res || res['error']){
        userStore.queueDelete(ts);
        // ↓ 仅在手动模式弹出报错，在自动模式下则由“自动下注方法包” 在判断后执行弹出提示
        if (!isAuto.value){
            showNegativeNotify(res?.['error']); 
        }
        return res;
    }
    const {oPromise, resolve, reject} = createPromise();
    res.response.amount ??= oForm.amount;
    oGameResult.value = {
        ...res.response,
        resolve,
        ts,
    };
    await oPromise;
    dealAnimDone(res.response.winAmount > 0, ts);
    joinStore.jackpotUpdate();
    const hasWonJackPot = jackpotHandler.value?.showJackpotWin?.();
    if (isAuto.value){
        const iDealy = hasWonJackPot ? 5_000 : 490;
        console.log("开局前暂停：", iDealy); 
        await Sleep(iDealy); // 自动模式要暂停一下
        jackpotHandler.value?.hideJackpotWin(); 
    } else {
        oForm.isSubmiting = false;
    }
    return res.response;
};

// ↓ 在动画播放完毕后调用 
const dealAnimDone = (hasWon, ts)=>{
    // console.log("子组件已将动画播放完毕，hasWon？", hasWon); 
    if (hasWon) {
        musicControler.oAudio.win?.toPlay();
    } else {
        musicControler.oAudio.lose?.toPlay();
    }
    userStore.queueForward(ts); // 显示余额变化 
    joinStore.showDateInQueue(); // 插入游戏结果
};

const autoBetToolKit = useAutoBetToolKit(oForm, betSubmit);

onMounted(() => {
    musicControler = new MusicControler(oMusicLib as any);
    onUnmounted(()=>{
        musicControler.toDestory();
    });
});
</script>


<script lang="ts">
export default defineComponent({
    name: 'diceGame',
});
</script>


<style lang="scss" scoped>
.dice-game {
    @media (max-width: 768px) {
        padding: 4px;
    }
}
.active {
    background-color: #ffc51c;
    color: #000;
}
</style>
